import type { ProjectScope } from './project-scope';

type LightingPendingCollection = 'lightingCues' | 'followSpotCues' | 'followSpots';

const pendingLightingWriteKeys = new Set<string>();
const pendingLightingDeleteKeys = new Set<string>();

function lightingWriteKey(scope: ProjectScope, collection: LightingPendingCollection, id: string): string {
    return `${scope.orgId}/${scope.projectId}/${collection}/${id}`;
}

function lightingCollectionPrefix(scope: ProjectScope, collection: LightingPendingCollection): string {
    return `${scope.orgId}/${scope.projectId}/${collection}/`;
}

function idFromLightingKey(
    scope: ProjectScope,
    collection: LightingPendingCollection,
    key: string
): string | null {
    const prefix = lightingCollectionPrefix(scope, collection);
    return key.startsWith(prefix) ? key.slice(prefix.length) : null;
}

function markPendingLightingWrite(scope: ProjectScope, collection: LightingPendingCollection, id: string): void {
    pendingLightingWriteKeys.add(lightingWriteKey(scope, collection, id));
    pendingLightingDeleteKeys.delete(lightingWriteKey(scope, collection, id));
}

function clearPendingLightingWrite(scope: ProjectScope, collection: LightingPendingCollection, id: string): void {
    pendingLightingWriteKeys.delete(lightingWriteKey(scope, collection, id));
}

function isPendingLightingWrite(scope: ProjectScope, collection: LightingPendingCollection, id: string): boolean {
    return pendingLightingWriteKeys.has(lightingWriteKey(scope, collection, id));
}

function markPendingLightingDelete(scope: ProjectScope, collection: LightingPendingCollection, id: string): void {
    pendingLightingDeleteKeys.add(lightingWriteKey(scope, collection, id));
    pendingLightingWriteKeys.delete(lightingWriteKey(scope, collection, id));
}

function isPendingLightingDelete(scope: ProjectScope, collection: LightingPendingCollection, id: string): boolean {
    return pendingLightingDeleteKeys.has(lightingWriteKey(scope, collection, id));
}

export function markPendingLightingCueWrite(scope: ProjectScope, cueId: string): void {
    markPendingLightingWrite(scope, 'lightingCues', cueId);
}

export function clearPendingLightingCueWrite(scope: ProjectScope, cueId: string): void {
    clearPendingLightingWrite(scope, 'lightingCues', cueId);
}

export function isPendingLightingCueWrite(scope: ProjectScope, cueId: string): boolean {
    return isPendingLightingWrite(scope, 'lightingCues', cueId);
}

export function markPendingFollowSpotCueWrite(scope: ProjectScope, cueId: string): void {
    markPendingLightingWrite(scope, 'followSpotCues', cueId);
}

export function clearPendingFollowSpotCueWrite(scope: ProjectScope, cueId: string): void {
    clearPendingLightingWrite(scope, 'followSpotCues', cueId);
}

export function isPendingFollowSpotCueWrite(scope: ProjectScope, cueId: string): boolean {
    return isPendingLightingWrite(scope, 'followSpotCues', cueId);
}

export function markPendingFollowSpotWrite(scope: ProjectScope, spotId: string): void {
    markPendingLightingWrite(scope, 'followSpots', spotId);
}

export function clearPendingFollowSpotWrite(scope: ProjectScope, spotId: string): void {
    clearPendingLightingWrite(scope, 'followSpots', spotId);
}

export function isPendingFollowSpotWrite(scope: ProjectScope, spotId: string): boolean {
    return isPendingLightingWrite(scope, 'followSpots', spotId);
}

export function markPendingFollowSpotDelete(scope: ProjectScope, spotId: string): void {
    markPendingLightingDelete(scope, 'followSpots', spotId);
}

export function isPendingFollowSpotDelete(scope: ProjectScope, spotId: string): boolean {
    return isPendingLightingDelete(scope, 'followSpots', spotId);
}

export function clearPendingFollowSpotDeletesMissingFromRemote(
    scope: ProjectScope,
    remoteSpotIds: ReadonlySet<string>
): void {
    for (const key of Array.from(pendingLightingDeleteKeys)) {
        const spotId = idFromLightingKey(scope, 'followSpots', key);
        if (spotId && !remoteSpotIds.has(spotId)) {
            pendingLightingDeleteKeys.delete(key);
        }
    }
}

export function clearPendingLightingWrites(): void {
    pendingLightingWriteKeys.clear();
    pendingLightingDeleteKeys.clear();
}
